import { useCallback, useEffect, useRef, useState } from "react";
import { PORTAL_EXAMS_API } from "../apis/apis";

/* ---------- Exam status (REQ-DASH-002) — same palette as the prototype ---------- */
export const STATUS_META = {
  available: { label: "Available", fill: "var(--accent-tint)", text: "var(--accent-text)" },
  scheduled: { label: "Scheduled", fill: "var(--accent-tint)", text: "var(--accent-text)" },
  "in-progress": { label: "In progress", fill: "var(--primary-tint)", text: "var(--primary-text)" },
  "in-review": { label: "In review", fill: "var(--accent-tint)", text: "var(--accent-text)" },
  completed: { label: "Completed", fill: "var(--success-tint)", text: "var(--success)" },
  failed: { label: "Failed", fill: "var(--danger-tint)", text: "var(--danger)" },
  expired: { label: "Expired", fill: "var(--line)", text: "var(--ink-faint)" },
};

export const JOURNEY_STEPS = [
  "Enroll in exam",
  "Book exam slot",
  "Prepare for exam",
  "Take exam",
  "Download transcript",
  "Submit for certification",
  "Certificate",
];

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
export const MONTH_ABBR = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export const getErr = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  (typeof error?.response?.data?.error === "string" ? error.response.data.error : null) ||
  (Array.isArray(error?.response?.data) ? error.response.data[0] : null) ||
  error?.response?.data?.non_field_errors?.[0] ||
  error?.message ||
  fallback;

export const encodeCode = (code) => {
  try {
    return btoa(code);
  } catch {
    return encodeURIComponent(code);
  }
};
export const decodeCode = (code) => {
  try {
    return atob(code);
  } catch {
    return decodeURIComponent(code || "");
  }
};

export const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "U";

export const firstName = (name = "") => (name || "").trim().split(/\s+/)[0] || "there";

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDate = (value) => {
  const d = toDate(value);
  if (!d) return null;
  return new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }).format(d);
};

export const formatDateTime = (value) => {
  const d = toDate(value);
  if (!d) return null;
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata",
  }).format(d);
};

/** "2026-11-12" + "2:00 PM IST" -> "November 12, 2026 at 2:00 PM IST" (prototype wording) */
export const bookingText = (booking) => {
  if (!booking?.date) return "—";
  const [y, m, d] = String(booking.date).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return `${booking.date} at ${booking.time || ""}`;
  return `${MONTH_NAMES[m - 1]} ${d}, ${y} at ${booking.time || ""}`.trim();
};

export const memberSinceLabel = (value) => {
  const d = toDate(value);
  if (!d) return null;
  return new Intl.DateTimeFormat("en-IN", { month: "short", year: "numeric" }).format(d);
};

export const fmtNum = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
};

/* ---------- live countdown ---------- */
export function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  const target = targetIso ? new Date(targetIso).getTime() : null;
  let diff = target ? Math.max(0, target - now) : 0;
  const days = Math.floor(diff / 86400000);
  diff %= 86400000;
  const hours = Math.floor(diff / 3600000);
  diff %= 3600000;
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  const pad = (v) => String(v).padStart(2, "0");
  return {
    done: !target || target <= now,
    days: pad(days),
    hours: pad(hours),
    minutes: pad(minutes),
    seconds: pad(seconds),
    hms: `${pad(days * 24 + hours)}:${pad(minutes)}:${pad(seconds)}`,
  };
}

/* ---------- portal exams loader (shared by My exams / History / Certification) ---------- */
export function usePortalExams() {
  const [state, setState] = useState({ loading: true, error: null, exams: [], memberId: null });
  const alive = useRef(true);
  const load = useCallback(async (silent = false) => {
    if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await PORTAL_EXAMS_API();
      if (res?.status === false) throw new Error(res?.message || "Unable to load your exams.");
      if (alive.current) {
        setState({ loading: false, error: null, exams: res?.data?.exams || [], memberId: res?.data?.member_id });
      }
    } catch (err) {
      if (alive.current) setState((s) => ({ ...s, loading: false, error: getErr(err, "Unable to load your exams.") }));
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);
  return { ...state, reload: load };
}

/* ---------- PDF documents (transcript / certificate) ---------- */
const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

async function renderPdf(html, filename, orientation = "portrait") {
  const { default: html2pdf } = await import("html2pdf.js");
  const holder = document.createElement("div");
  holder.innerHTML = html;
  holder.style.position = "fixed";
  holder.style.left = "-10000px";
  holder.style.top = "0";
  document.body.appendChild(holder);
  try {
    await html2pdf()
      .set({
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation },
      })
      .from(holder.firstElementChild)
      .save();
  } finally {
    holder.remove();
  }
}

export async function downloadTranscript({ user, exam, attempt }) {
  const r = attempt?.result || {};
  const domains = (r.domain_performance || [])
    .map(
      (d) => `<tr><td style="padding:7px 10px;border-bottom:1px solid #E3E7F0">${esc(d.domain_name || "—")}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #E3E7F0;text-align:center">${esc(d.questions_count ?? "—")}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #E3E7F0;text-align:right">${esc(fmtNum(d.percentage))}%</td></tr>`
    )
    .join("");
  const passed = r.is_passed;
  const html = `
  <div style="width:210mm;min-height:296mm;padding:18mm 16mm;box-sizing:border-box;font-family:Inter,Arial,sans-serif;color:#0B0D12;background:#fff">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #16FCFB;padding-bottom:12px">
      <div><div style="font-size:22px;font-weight:800">Hacktonomy</div><div style="font-size:11px;color:#5B6272">Certification Body</div></div>
      <div style="text-align:right"><div style="font-size:16px;font-weight:800">Official Exam Transcript</div>
      <div style="font-size:11px;color:#5B6272">Generated ${esc(formatDateTime(new Date()))} IST</div></div>
    </div>
    <table style="width:100%;margin-top:18px;font-size:12.5px;border-collapse:collapse">
      <tr><td style="color:#5B6272;padding:4px 0;width:38%">Candidate</td><td style="font-weight:700">${esc(user?.name)}</td></tr>
      <tr><td style="color:#5B6272;padding:4px 0">Member ID</td><td>${esc(user?.member_id)}</td></tr>
      <tr><td style="color:#5B6272;padding:4px 0">Examination</td><td style="font-weight:700">${esc(exam?.full_name)} (${esc(exam?.exam_code)})</td></tr>
      <tr><td style="color:#5B6272;padding:4px 0">Attempt</td><td>${esc(attempt?.attempt_number)} · ${esc(formatDateTime(attempt?.date) || attempt?.slot_date || "—")}</td></tr>
      <tr><td style="color:#5B6272;padding:4px 0">Result verification ID</td><td style="font-family:monospace">${esc(r.verification_id || "—")}</td></tr>
    </table>
    <div style="display:flex;gap:10px;margin-top:20px">
      ${[
      ["Total marks", fmtNum(r.maximum_score)],
      ["Marks gained", fmtNum(r.score)],
      ["Percentage", `${fmtNum(r.percentage)}%`],
      ["Passing score", `${fmtNum(r.passing_percentage)}%`],
    ]
      .map(
        ([l, v]) => `<div style="flex:1;border:1px solid #E3E7F0;border-radius:10px;padding:12px;text-align:center">
          <div style="font-size:10.5px;color:#5B6272">${l}</div><div style="font-size:19px;font-weight:800;margin-top:4px">${esc(v)}</div></div>`
      )
      .join("")}
    </div>
    <div style="margin-top:14px;padding:10px 14px;border-radius:8px;font-weight:800;font-size:13px;background:${passed ? "#E5F5F0" : "#FBEAE7"};color:${passed ? "#1F8A70" : "#C0392B"}">
      Result: ${passed ? "Provisional Pass" : "FAIL"} · Questions answered ${esc(fmtNum(r.answered_questions))} / ${esc(fmtNum(r.total_questions))}
    </div>
    ${domains
      ? `<div style="margin-top:22px;font-size:13px;font-weight:800">Domain performance</div>
      <table style="width:100%;margin-top:8px;font-size:12px;border-collapse:collapse">
        <tr style="background:#F3F5F8"><th style="text-align:left;padding:7px 10px">Domain</th><th style="padding:7px 10px">Questions</th><th style="text-align:right;padding:7px 10px">Score</th></tr>
        ${domains}
      </table>`
      : ""
    }
    <div style="margin-top:28px;font-size:10.5px;color:#99A0AF">This transcript is generated from the Hacktonomy examination record. Verify authenticity with the result verification ID above.</div>
  </div>`;
  await renderPdf(html, `${exam?.exam_code || "exam"}-transcript-attempt-${attempt?.attempt_number || 1}.pdf`);
}

export const certificateVerifyUrl = (certificateId) =>
  `${window.location.origin}/verify-certificate/${encodeURIComponent(certificateId || "")}`;

/** QR code (as an SVG data URL) rendered with antd's QRCode component. */
export async function qrDataUrl(value, size = 140) {
  const [{ createRoot }, { flushSync }, React, { QRCode }] = await Promise.all([
    import("react-dom/client"),
    import("react-dom"),
    import("react"),
    import("antd"),
  ]);
  const el = document.createElement("div");
  el.style.position = "fixed";
  el.style.left = "-10000px";
  document.body.appendChild(el);
  const root = createRoot(el);
  try {
    flushSync(() => {
      root.render(
        React.createElement(QRCode, { value, type: "svg", size, bordered: false, color: "#0B0D12", bgColor: "#FFFFFF", errorLevel: "M" })
      );
    });
    await new Promise((r) => setTimeout(r, 30));
    const svg = el.querySelector("svg");
    if (!svg) return null;
    if (!svg.getAttribute("xmlns")) svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    const xml = new XMLSerializer().serializeToString(svg);
    return `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(xml)))}`;
  } finally {
    root.unmount();
    el.remove();
  }
}

export async function downloadCertificate({ user, exam, certificate, logoSrc }) {
  const verifyUrl = certificateVerifyUrl(certificate?.certificate_id);
  let qr = null;
  try {
    qr = await qrDataUrl(verifyUrl);
  } catch (e) {
    qr = null;
  }
  const html = `
  <div style="width:297mm;height:209mm;box-sizing:border-box;padding:14mm;background:#fff;font-family:Manrope,Inter,Arial,sans-serif">
    <div style="height:100%;box-sizing:border-box;border:3px solid #0B0D12;outline:1px solid #16FCFB;outline-offset:-10px;padding:18mm 20mm;text-align:center;color:#0B0D12;position:relative">
      ${logoSrc ? `<img src="${esc(logoSrc)}" style="width:64px;height:64px;object-fit:contain" />` : ""}
      <div style="font-size:13px;letter-spacing:.3em;color:#5B6272;margin-top:8px">HACKTONOMY</div>
      <div style="font-size:34px;font-weight:800;margin-top:14px">Certificate of Achievement</div>
      <div style="font-size:14px;color:#5B6272;margin-top:18px">This certifies that</div>
      <div style="font-size:30px;font-weight:800;margin-top:8px;border-bottom:1px solid #E3E7F0;display:inline-block;padding:0 30px 6px">${esc(certificate?.full_name || user?.name)}</div>
      <div style="font-size:14px;color:#5B6272;margin-top:16px">has successfully completed</div>
      <div style="font-size:20px;font-weight:800;margin-top:8px">${esc(exam?.full_name)} (${esc(exam?.exam_code)}) - Certification</div>
      ${qr
      ? `<div style="position:absolute;right:20mm;top:16mm;text-align:center;font-size:9px;color:#5B6272;font-family:monospace">
          <img src="${qr}" style="width:30mm;height:30mm;display:block" />
          <div style="margin-top:3px">Scan to verify</div></div>`
      : ""
    }
      <div style="position:absolute;left:20mm;right:20mm;bottom:9mm;text-align:center;font-size:9.5px;color:#99A0AF;font-family:monospace">Verify at ${esc(verifyUrl)}</div>
      <div style="position:absolute;left:20mm;right:20mm;bottom:16mm;display:flex;justify-content:space-between;font-size:11px;color:#5B6272;font-family:monospace">
        <span>Certificate ID: ${esc(certificate?.certificate_id)}</span>
        <span>Member ID: ${esc(user?.member_id)}</span>
        <span>Issued: ${esc(formatDate(certificate?.issued_at) || "—")}</span>
      </div>
    </div>
  </div>`;
  await renderPdf(html, `${certificate?.certificate_id || "certificate"}.pdf`, "landscape");
}
